import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    inject,
    Signal
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { DatePipe, NgClass, NgIf, NgForOf, NgTemplateOutlet } from '@angular/common';

import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subject, takeUntil } from 'rxjs';
import { NotificationDTO, NotificationSeverity } from '../../../core/models/notification.types';
import { NotificationStore } from '../../../core/services/notification.store';
import { MatButton } from '@angular/material/button';
import { ViewContainerRef, ViewChild, TemplateRef } from '@angular/core';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    standalone: true,
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        TranslocoDirective,
        DatePipe,
        NgClass,
        NgIf,
        NgForOf,
        NgTemplateOutlet,
        RouterLink
    ]
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    public readonly store = inject(NotificationStore);
    private readonly overlay = inject(Overlay);
    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly router = inject(Router);

    constructor() { }

    ngOnInit(): void {
        // Overlay will be created on open to ensure origin is ready
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        if (!this._notificationsPanel || !this._notificationsOrigin) return;

        if (!this._overlayRef) {
            this._createOverlay();
        }

        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this.viewContainerRef));
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void {
        if (this._overlayRef && this._overlayRef.hasAttached()) {
            this._overlayRef.detach();
        }
    }

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        this._overlayRef = this.overlay.create({
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            scrollStrategy: this.overlay.scrollStrategies.block(),
            positionStrategy: this.overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        this._overlayRef.backdropClick().subscribe(() => {
            this.closePanel();
        });
    }

    //---------------------------------------------------------
    // User Actions
    //---------------------------------------------------------

    markAllAsRead(): void {
        this.store.markAllAsRead();
    }

    markAsRead(notification: NotificationDTO, event: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }
        if (!notification.read) {
            this.store.markAsRead(notification.id);
        }
    }

    deleteNotification(notification: NotificationDTO, event: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }
        this.store.deleteNotification(notification.id);
    }

    clickNotification(notification: NotificationDTO): void {
        if (!notification.read) {
            this.store.markAsRead(notification.id);
        }

        if (notification.actionUrl) {
            this.router.navigateByUrl(notification.actionUrl);
            this.closePanel();
        }
    }

    loadMore(): void {
        this.store.loadMore();
    }

    //---------------------------------------------------------
    // Format Helpers
    //---------------------------------------------------------

    getSeverityClasses(severity: NotificationSeverity): string {
        switch (severity) {
            case NotificationSeverity.SUCCESS: return 'bg-green-500';
            case NotificationSeverity.WARNING: return 'bg-amber-500';
            case NotificationSeverity.ERROR: return 'bg-red-500';
            case NotificationSeverity.INFO:
            default: return 'bg-blue-500';
        }
    }

    getRelativeTime(dateStr: string): string {
        if (!dateStr) return '';

        const date = new Date(dateStr);
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (diffInSeconds < 60) {
            return 'just_now';
        }

        const diffInMinutes = Math.floor(diffInSeconds / 60);
        if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        }

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) {
            return `${diffInHours}h ago`;
        }

        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays}d ago`;
    }
}
